import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  Card,
  Form,
  Select,
  Switch,
  Button,
  Typography,
  message,
  TimePicker,
  Alert,
  Descriptions,
  Progress,
  Skeleton,
} from "antd";
import moment from "moment";
import {
  updateOrganizationClientValidationStats,
  recomputeOrganizationClientValidationStats,
  getOrganizationClientValidationStatsStatus,
  listOrganizationsClientValidationStatsSettings,
  previewClientContactStorageMigration,
  migrateClientContactStorage,
  getClientContactStorageMigrationStatus,
} from "../../../api/Services";
import AdminValidation from "../../../components/adminvalidation/AdminValidation";
import { Modal, Table, Space, Input, Tag } from "antd";

const { Title, Text } = Typography;

type Frequency = "HOURLY" | "DAILY";

type OrgSettings = {
  id: string;
  name: string;
  clientValidationStatsEnabled?: boolean;
  clientValidationStatsRefreshFrequency?: Frequency;
  clientValidationStatsDailyHour?: number;
  clientValidationStatsDailyMinute?: number;
  clientValidationStatsHourlyMinute?: number;
};

const MINUTES = Array.from({ length: 60 }, (_, i) => ({
  label: i.toString().padStart(2, "0"),
  value: i,
}));

type OrgRow = OrgSettings & {
  key: string;
};

type ContactMigrationPreview = {
  organizationId: number;
  organizationName: string;
  totalClients: number;
  clientsWithStoredContact: number;
  storedEmails: number;
  storedPhones: number;
  storedMobiles: number;
  validEmails: number;
  validPhones: number;
  validMobiles: number;
};

type ContactMigrationRun = {
  id: number;
  organizationId: number;
  organizationName: string;
  status: "PENDING" | "RUNNING" | "SUCCESS" | "FAILED";
  totalClients: number;
  clientsToMigrate: number;
  migratedClients: number;
  percentCompleted: number;
  errorMessage?: string | null;
  active: boolean;
};

type AdminClientValidationStatsProps = {
  embedded?: boolean;
};

const AdminClientValidationStats = ({
  embedded = false,
}: AdminClientValidationStatsProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<OrgRow[]>([]);
  const [filter, setFilter] = useState("");
  const [selectedOrg, setSelectedOrg] = useState<OrgRow | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [frequency, setFrequency] = useState<Frequency>("DAILY");
  const [computeStatus, setComputeStatus] = useState<string | null>(null);
  const [computeError, setComputeError] = useState<string | null>(null);
  const [migrationOrg, setMigrationOrg] = useState<OrgRow | null>(null);
  const [migrationPreview, setMigrationPreview] =
    useState<ContactMigrationPreview | null>(null);
  const [migrationVisible, setMigrationVisible] = useState(false);
  const [migrationLoading, setMigrationLoading] = useState(false);
  const [migrationConfirmation, setMigrationConfirmation] = useState("");
  const [migrationRun, setMigrationRun] =
    useState<ContactMigrationRun | null>(null);
  const pollRef = useRef<number | null>(null);

  const [form] = Form.useForm();

  const refreshTable = useCallback(
    async (page: number = 1, size: number = 10, q: string = filter) => {
      setLoading(true);
      try {
        const list = await listOrganizationsClientValidationStatsSettings(
          page - 1,
          size,
          q
        );
        const pageObj: any = list?.content
          ? list
          : list?.data?.content
          ? list.data
          : list;
        const arr: any[] = pageObj?.content ?? [];
        const mapped: OrgRow[] = (arr || [])
          .filter((r: any) => r?.organizationId != null)
          .map((r: any) => ({
            key: String(r.organizationId),
            id: String(r.organizationId),
            name: String(r.organizationName ?? ""),
            clientValidationStatsEnabled: !!r.enabled,
            clientValidationStatsRefreshFrequency: (r.refreshFrequency ||
              "DAILY") as Frequency,
            clientValidationStatsDailyHour:
              typeof r.dailyHour === "number" ? r.dailyHour : 2,
            clientValidationStatsDailyMinute:
              typeof r.dailyMinute === "number" ? r.dailyMinute : 0,
            clientValidationStatsHourlyMinute:
              typeof r.hourlyMinute === "number" ? r.hourlyMinute : 0,
          }));
        setRows(mapped);
        const totalElements =
          typeof pageObj?.totalElements === "number"
            ? pageObj.totalElements
            : mapped.length;
        setPagination((prev) => ({
          ...prev,
          current: page,
          pageSize: size,
          total: totalElements,
        }));
      } finally {
        setLoading(false);
      }
    },
    [filter]
  );

  useEffect(() => {
    refreshTable();
  }, [refreshTable]);

  const openEditModal = useCallback(
    async (row: OrgRow) => {
      setSelectedOrg(row);
      setFrequency(row.clientValidationStatsRefreshFrequency ?? "DAILY");
      setComputeStatus(null);
      setComputeError(null);
      form.setFieldsValue({
        enabled: row.clientValidationStatsEnabled,
        frequency: row.clientValidationStatsRefreshFrequency,
        dailyTime: moment()
          .hour(row.clientValidationStatsDailyHour ?? 2)
          .minute(row.clientValidationStatsDailyMinute ?? 0)
          .second(0),
        hourlyMinute: row.clientValidationStatsHourlyMinute,
      });
      setIsModalVisible(true);
    },
    [form]
  );

  const canSave = useMemo(() => !!selectedOrg, [selectedOrg]);

  const openMigrationPreview = useCallback(
    async (row: OrgRow) => {
      setMigrationOrg(row);
      setMigrationPreview(null);
      setMigrationRun(null);
      setMigrationConfirmation("");
      setMigrationVisible(true);
      setMigrationLoading(true);
      try {
        const [preview, latestRun] = await Promise.all([
          previewClientContactStorageMigration(row.id),
          getClientContactStorageMigrationStatus(row.id),
        ]);
        setMigrationPreview(preview);
        if (latestRun?.active || latestRun?.status === "FAILED") {
          setMigrationRun(latestRun);
        }
      } catch (error) {
        const apiMessage =
          (error as any)?.response?.data?.apierror?.message_ES ??
          (error as any)?.response?.data?.apierror?.message;
        message.error(
          apiMessage ?? t("AdminClientValidationStats-Migration-PreviewError")
        );
        setMigrationVisible(false);
      } finally {
        setMigrationLoading(false);
      }
    },
    [t]
  );

  const onMigrateContactStorage = useCallback(async () => {
    if (!migrationOrg || migrationConfirmation !== migrationOrg.name) return;
    setMigrationLoading(true);
    try {
      const run = await migrateClientContactStorage(
        migrationOrg.id,
        migrationConfirmation
      );
      setMigrationRun(run);
      setMigrationConfirmation("");
      if (run?.status === "SUCCESS") {
        message.success(
          t("AdminClientValidationStats-Migration-Success", {
            count: run?.migratedClients ?? 0,
          })
        );
        setMigrationVisible(false);
      } else {
        message.info(t("AdminClientValidationStats-Migration-Queued"));
      }
    } catch (error) {
      const apiMessage =
        (error as any)?.response?.data?.apierror?.message_ES ??
        (error as any)?.response?.data?.apierror?.message;
      message.error(
        apiMessage ?? t("AdminClientValidationStats-Migration-Error")
      );
    } finally {
      setMigrationLoading(false);
    }
  }, [migrationConfirmation, migrationOrg, t]);

  useEffect(() => {
    if (
      !migrationVisible ||
      !migrationOrg ||
      (migrationRun?.status !== "PENDING" &&
        migrationRun?.status !== "RUNNING")
    ) {
      return;
    }

    let disposed = false;
    const tick = async () => {
      try {
        const latestRun = await getClientContactStorageMigrationStatus(
          migrationOrg.id
        );
        if (disposed || !latestRun) return;
        setMigrationRun(latestRun);
        if (latestRun.status === "SUCCESS") {
          message.success(
            t("AdminClientValidationStats-Migration-Success", {
              count: latestRun.migratedClients ?? 0,
            })
          );
          setMigrationVisible(false);
        } else if (latestRun.status === "FAILED") {
          message.error(t("AdminClientValidationStats-Migration-Error"));
        }
      } catch (_error) {
        // A transient polling failure must not turn a running migration into a failure.
      }
    };

    void tick();
    const intervalId = window.setInterval(tick, 2500);
    return () => {
      disposed = true;
      window.clearInterval(intervalId);
    };
  }, [migrationOrg, migrationRun?.status, migrationVisible, t]);

  const onSave = useCallback(async () => {
    if (!selectedOrg) return;
    const values = await form.validateFields();
    setLoading(true);
    try {
      const dailyTime =
        values.dailyTime && typeof values.dailyTime.hour === "function"
          ? values.dailyTime
          : moment().hour(2).minute(0).second(0);
      const dailyHour = Number(dailyTime.hour());
      const dailyMinute = Number(dailyTime.minute());

      const ok = await updateOrganizationClientValidationStats(selectedOrg.id, {
        enabled: !!values.enabled,
        refreshFrequency: values.frequency as Frequency,
        dailyHour,
        dailyMinute,
        hourlyMinute: Number(values.hourlyMinute ?? 0),
      });
      if (ok) {
        message.success(t("Common-Uptade-Success"));
        setRows((prev) =>
          prev.map((r) =>
            r.id === selectedOrg.id
              ? {
                  ...r,
                  clientValidationStatsEnabled: !!values.enabled,
                  clientValidationStatsRefreshFrequency:
                    values.frequency as Frequency,
                  clientValidationStatsDailyHour: dailyHour,
                  clientValidationStatsDailyMinute: dailyMinute,
                  clientValidationStatsHourlyMinute: Number(
                    values.hourlyMinute ?? 0
                  ),
                }
              : r
          )
        );
        setIsModalVisible(false);
      } else {
        message.error(t("Error-CommonError"));
      }
    } finally {
      setLoading(false);
    }
  }, [form, selectedOrg, t]);

  const onRecomputeNow = useCallback(async () => {
    if (!selectedOrg) return;
    const enabled = !!form.getFieldValue("enabled");
    if (!enabled) {
      message.info(t("AdminClientValidationStats-ComputeNow-Disabled"));
      return;
    }
    setLoading(true);
    try {
      setComputeError(null);
      setComputeStatus("RUNNING");
      await recomputeOrganizationClientValidationStats(selectedOrg.id);
      message.success(t("AdminClientValidationStats-ComputeNow-Started"));
    } catch (e) {
      message.error(t("AdminClientValidationStats-ComputeNow-Error"));
      setComputeStatus("FAILED");
    } finally {
      setLoading(false);
    }
  }, [form, selectedOrg, t]);

  useEffect(() => {
    if (!selectedOrg) return;
    if (computeStatus !== "RUNNING") return;

    const tick = async () => {
      const statusResp = await getOrganizationClientValidationStatsStatus(
        selectedOrg.id
      );
      const data = statusResp?.data ?? statusResp;
      const status = data?.status ?? data?.status?.name ?? null;
      setComputeStatus(status);
      setComputeError(data?.errorMessage ?? null);

      if (status && status !== "RUNNING" && pollRef.current != null) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
        if (status === "SUCCESS") {
          message.success(t("AdminClientValidationStats-ComputeNow-Success"));
        } else if (status === "FAILED") {
          message.error(t("AdminClientValidationStats-ComputeNow-Error"));
        }
      }
    };

    // run once immediately, then poll
    tick();
    pollRef.current = window.setInterval(tick, 3000);

    return () => {
      if (pollRef.current != null) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [computeStatus, selectedOrg, t]);

  const migrationActive =
    migrationRun?.status === "PENDING" || migrationRun?.status === "RUNNING";

  return (
    <AdminValidation>
      <div style={{ padding: embedded ? 0 : 24 }}>
        {!embedded && (
          <Title level={3}>{t("AdminClientValidationStats-Title")}</Title>
        )}
        {embedded && (
          <Text type="secondary">
            {t("AdminClientValidationStats-ManagementDescription")}
          </Text>
        )}
        <Card>
          <Space style={{ marginBottom: 12 }}>
            <Input
              placeholder={t("Common-Search")}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              style={{ width: 260 }}
              allowClear
            />
            <Button
              onClick={() => refreshTable(1, pagination.pageSize, filter)}
              loading={loading}
            >
              {t("Common-Refresh")}
            </Button>
          </Space>
          <Table
            rowKey="id"
            loading={loading}
            dataSource={rows}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              onChange: (page, pageSize) =>
                refreshTable(page, pageSize, filter),
            }}
            scroll={{ x: 760 }}
            columns={[
              {
                title: t("AdminClientValidationStats-Org"),
                dataIndex: "name",
                key: "name",
              },
              {
                title: t("AdminClientValidationStats-Enabled"),
                dataIndex: "clientValidationStatsEnabled",
                key: "enabled",
                render: (v: boolean) =>
                  v ? <Tag color="green">ON</Tag> : <Tag color="red">OFF</Tag>,
              },
              {
                title: t("AdminClientValidationStats-Frequency"),
                dataIndex: "clientValidationStatsRefreshFrequency",
                key: "freq",
              },
              {
                title: t("AdminClientValidationStats-Schedule"),
                key: "schedule",
                render: (_: any, r: OrgRow) => {
                  const f = r.clientValidationStatsRefreshFrequency ?? "DAILY";
                  if (f === "HOURLY") {
                    return `:${String(
                      r.clientValidationStatsHourlyMinute ?? 0
                    ).padStart(2, "0")}`;
                  }
                  const hh = String(
                    r.clientValidationStatsDailyHour ?? 2
                  ).padStart(2, "0");
                  const mm = String(
                    r.clientValidationStatsDailyMinute ?? 0
                  ).padStart(2, "0");
                  return `${hh}:${mm}`;
                },
              },
              {
                title: t("Common-Actions"),
                key: "actions",
                render: (_: any, r: OrgRow) => (
                  <Space wrap>
                    <Button size="small" onClick={() => openEditModal(r)}>
                      {t("Common-Edit")}
                    </Button>
                    <Button
                      size="small"
                      danger
                      onClick={() => openMigrationPreview(r)}
                    >
                      {t("AdminClientValidationStats-Migration-Action")}
                    </Button>
                  </Space>
                ),
              },
            ]}
          />
        </Card>

        <Modal
          title={
            selectedOrg
              ? `${t("AdminClientValidationStats-SettingsTitle")}: ${
                  selectedOrg.name
                }`
              : t("AdminClientValidationStats-SettingsTitle")
          }
          visible={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          footer={
            <Space>
              <Button
                onClick={() => setIsModalVisible(false)}
                disabled={loading}
              >
                {t("Common-Cancel")}
              </Button>
              <Button
                onClick={onRecomputeNow}
                disabled={!canSave || !form.getFieldValue("enabled")}
                loading={loading}
              >
                {t("AdminClientValidationStats-ComputeNow")}
              </Button>
              <Button
                type="primary"
                onClick={onSave}
                disabled={!canSave}
                loading={loading}
              >
                {t("Common-Save")}
              </Button>
            </Space>
          }
        >
          {!selectedOrg ? null : (
            <Form
              layout="vertical"
              form={form}
              onValuesChange={(changedValues) => {
                if (changedValues?.frequency) {
                  setFrequency(changedValues.frequency as Frequency);
                }
              }}
            >
              <Form.Item
                label={t("AdminClientValidationStats-Enabled")}
                name="enabled"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item
                label={t("AdminClientValidationStats-Frequency")}
                name="frequency"
                rules={[{ required: true }]}
              >
                <Select
                  options={[
                    {
                      label: t("AdminClientValidationStats-FrequencyHourly"),
                      value: "HOURLY",
                    },
                    {
                      label: t("AdminClientValidationStats-FrequencyDaily"),
                      value: "DAILY",
                    },
                  ]}
                />
              </Form.Item>

              {frequency === "DAILY" && (
                <Form.Item
                  label={t("AdminClientValidationStats-DailyTime")}
                  name="dailyTime"
                  rules={[{ required: true }]}
                >
                  <TimePicker format="HH:mm" style={{ width: "100%" }} />
                </Form.Item>
              )}

              {frequency === "HOURLY" && (
                <Form.Item
                  label={t("AdminClientValidationStats-HourlyMinute")}
                  name="hourlyMinute"
                  rules={[{ required: true }]}
                >
                  <Select options={MINUTES} />
                </Form.Item>
              )}

              <Text type="secondary">
                {t("AdminClientValidationStats-TimezoneNote")}
              </Text>
              {computeStatus && (
                <div style={{ marginTop: 12 }}>
                  <Text type="secondary">
                    {t("AdminClientValidationStats-ComputeNow-Status")}{" "}
                    <b>{computeStatus}</b>
                  </Text>
                  {computeError && (
                    <div>
                      <Text type="danger">{computeError}</Text>
                    </div>
                  )}
                </div>
              )}
            </Form>
          )}
        </Modal>

        <Modal
          title={t("AdminClientValidationStats-Migration-Title", {
            organization: migrationOrg?.name ?? "",
          })}
          visible={migrationVisible}
          onCancel={() => setMigrationVisible(false)}
          width={680}
          footer={
            <Space wrap>
              <Button
                onClick={() => setMigrationVisible(false)}
                disabled={migrationLoading}
              >
                {t(migrationActive ? "Common-Close" : "Common-Cancel")}
              </Button>
              <Button
                danger
                type="primary"
                loading={migrationLoading}
                disabled={
                  !migrationPreview ||
                  migrationPreview.clientsWithStoredContact === 0 ||
                  migrationActive ||
                  migrationConfirmation !== migrationOrg?.name
                }
                onClick={onMigrateContactStorage}
              >
                {t("AdminClientValidationStats-Migration-ConfirmAction")}
              </Button>
            </Space>
          }
        >
          <Alert
            type="warning"
            showIcon
            message={t("AdminClientValidationStats-Migration-WarningTitle")}
            description={t("AdminClientValidationStats-Migration-Warning")}
            style={{ marginBottom: 16 }}
          />

          {migrationActive && migrationRun && (
            <Alert
              type="info"
              showIcon
              message={t(
                migrationRun.status === "PENDING"
                  ? "AdminClientValidationStats-Migration-Queued"
                  : "AdminClientValidationStats-Migration-Running"
              )}
              description={
                <div style={{ marginTop: 8 }}>
                  <Progress
                    percent={Math.round(migrationRun.percentCompleted ?? 0)}
                    status="active"
                  />
                  <Text type="secondary">
                    {t("AdminClientValidationStats-Migration-Progress", {
                      processed: migrationRun.migratedClients ?? 0,
                      total: migrationRun.clientsToMigrate ?? 0,
                    })}
                  </Text>
                </div>
              }
              style={{ marginBottom: 16 }}
            />
          )}

          {migrationRun?.status === "FAILED" && (
            <Alert
              type="error"
              showIcon
              message={t("AdminClientValidationStats-Migration-Failed")}
              style={{ marginBottom: 16 }}
            />
          )}

          {migrationLoading && !migrationPreview && (
            <Skeleton active paragraph={{ rows: 5 }} />
          )}

          {migrationPreview && (
            <>
              <Descriptions bordered size="small" column={1}>
                <Descriptions.Item
                  label={t("AdminClientValidationStats-Migration-TotalClients")}
                >
                  {migrationPreview.totalClients}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t(
                    "AdminClientValidationStats-Migration-AffectedClients"
                  )}
                >
                  {migrationPreview.clientsWithStoredContact}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t("AdminClientValidationStats-Migration-Emails")}
                >
                  {migrationPreview.storedEmails} (
                  {migrationPreview.validEmails}{" "}
                  {t("AdminClientValidationStats-Migration-Valid")})
                </Descriptions.Item>
                <Descriptions.Item
                  label={t("AdminClientValidationStats-Migration-Phones")}
                >
                  {migrationPreview.storedPhones} (
                  {migrationPreview.validPhones}{" "}
                  {t("AdminClientValidationStats-Migration-Valid")})
                </Descriptions.Item>
                <Descriptions.Item
                  label={t("AdminClientValidationStats-Migration-Mobiles")}
                >
                  {migrationPreview.storedMobiles} (
                  {migrationPreview.validMobiles}{" "}
                  {t("AdminClientValidationStats-Migration-Valid")})
                </Descriptions.Item>
              </Descriptions>

              {!migrationActive &&
                (migrationPreview.clientsWithStoredContact === 0 ? (
                  <Alert
                    type="info"
                    showIcon
                    message={t(
                      "AdminClientValidationStats-Migration-NothingToDo"
                    )}
                    style={{ marginTop: 16 }}
                  />
                ) : (
                  <div style={{ marginTop: 16 }}>
                    <Text>
                      {t("AdminClientValidationStats-Migration-TypeOrgName", {
                        organization: migrationOrg?.name ?? "",
                      })}
                    </Text>
                    <Input
                      value={migrationConfirmation}
                      onChange={(event) =>
                        setMigrationConfirmation(event.target.value)
                      }
                      placeholder={migrationOrg?.name}
                      autoComplete="off"
                      style={{ marginTop: 8 }}
                    />
                  </div>
                ))}
            </>
          )}
        </Modal>
      </div>
    </AdminValidation>
  );
};

export default AdminClientValidationStats;
